import { useState } from "react";
import { ArrowDownAZ, ArrowUpAZ, ChevronDown, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { etichetaValoare, valoriDistincte } from "@/utils/targetareRaport";
import type { ColoanaTabel, RandRaport } from "@/utils/targetareTabel";

type MeniuAntetProps<R extends RandRaport> = {
  coloane: ColoanaTabel<R>[];
  coloana: ColoanaTabel<R>;
  randuri: R[];
  onSorteaza: (id: string, desc: boolean) => void;
  onAscunde: (id: string) => void;
  onFiltruValoare: (id: string, valoare: string) => void;
};

export function MeniuAntet<R extends RandRaport>({
  coloane,
  coloana,
  randuri,
  onSorteaza,
  onAscunde,
  onFiltruValoare,
}: MeniuAntetProps<R>) {
  const [deschis, setDeschis] = useState(false);
  const [cautare, setCautare] = useState("");
  const valori =
    deschis && coloana.tip !== "numar"
      ? valoriDistincte(randuri, coloana.id, 300)
      : [];
  const filtrate = valori
    .filter((v) => v.toLowerCase().includes(cautare.toLowerCase()))
    .slice(0, 60);
  return (
    <Popover open={deschis} onOpenChange={setDeschis}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="ml-auto rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={`Meniul coloanei ${coloana.eticheta}`}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="flex items-center gap-1 border-b pb-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            title="Sortare crescătoare"
            onClick={() => {
              onSorteaza(coloana.id, false);
              setDeschis(false);
            }}
          >
            <ArrowUpAZ className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            title="Sortare descrescătoare"
            onClick={() => {
              onSorteaza(coloana.id, true);
              setDeschis(false);
            }}
          >
            <ArrowDownAZ className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            title="Ascunde coloana"
            onClick={() => {
              onAscunde(coloana.id);
              setDeschis(false);
            }}
          >
            <EyeOff className="h-4 w-4" />
          </Button>
          <span className="ml-auto truncate text-xs font-medium">
            {coloana.eticheta}
          </span>
        </div>
        {coloana.tip !== "numar" && (
          <div className="pt-2">
            <Input
              value={cautare}
              onChange={(e) => setCautare(e.target.value)}
              placeholder="caută valori"
              className="mb-1 h-7 text-xs"
              aria-label="Caută valori"
            />
            <ul className="max-h-56 overflow-y-auto text-sm">
              {filtrate.map((v) => (
                <li key={v}>
                  <button
                    type="button"
                    className="w-full truncate rounded px-1 py-0.5 text-left hover:bg-muted"
                    title={`Filtrează: ${coloana.eticheta} este ${v}`}
                    onClick={() => {
                      onFiltruValoare(coloana.id, v);
                      setDeschis(false);
                    }}
                  >
                    {etichetaValoare(coloane, coloana.id, v)}
                  </button>
                </li>
              ))}
              {filtrate.length === 0 && (
                <li className="px-1 py-1 text-xs text-muted-foreground">
                  nicio valoare
                </li>
              )}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
